import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  deletePensionCalculation,
  getPensionCalculations,
  updatePensionCalculation,
  updatePensionCalculationStatusAsync
} from '../../services/pension'
import { formatFriendlyDate } from '../../config/resources/utils'
import { getLogger } from '../../core/debuggers'

const logger = getLogger('PensionCalculation.slice.js')

const initialState = {
  error: null,
  loading: false,
  updatingId: null,
  pensionCalculations: [],
  projectToEdit: null,
  status: 'idle'
}

/**
 * Gets the list of pension calculations.
 */
export const onGetPensionCalculationsAsync = createAsyncThunk(
'adminPensionCalculations/getCalculations',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getPensionCalculations()
      return data
    } catch (err) {
      logger.error('Failed to fetch pension calculations', err)
      return rejectWithValue({ message: err.message })
    }
  }
)

/**
 * Update a pension calculation.
 */
export const onEditPensionCalculationAsync = createAsyncThunk(
  'adminPensionCalculations/editPensionCalculation',
  async ({ id, updatedObject }, { getState, rejectWithValue }) => {
    try {
      const state = getState()
      const response = await updatePensionCalculation({ id, updatedObject })

      const idx = state.adminPensionCalculations.pensionCalculations.findIndex(item => item.id === id)
      if (idx !== -1) {
        const updatedArray = [...state.adminPensionCalculations.pensionCalculations]
        updatedArray[idx] = { ...updatedArray[idx], ...updatedObject }
        return updatedArray
      }
      return response
    } catch (err) {
      logger.error('Failed to edit pension calculation', err)
      return rejectWithValue({ message: err.message })
    }
  }
)

/**
 * Update pension calculation status.
 */
export const onUpdatePensionCalculationStatusAsync = createAsyncThunk(
  'adminPensionCalculations/updateStatus',
  async ({ id, status, paymentAt }, { rejectWithValue }) => {
    try {
      const response = await updatePensionCalculationStatusAsync({ id, status, paymentAt })
      return response
    } catch (err) {
      logger.error('Failed to update pension calculation status', err)
      return rejectWithValue({ message: err.message })
    }
  }
)

/**
 * Delete a pension calculation.
 */
export const onDeletePensionCalculationAsync = createAsyncThunk(
  'adminPensionCalculations/deletePensionCalculation',
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await deletePensionCalculation({ id })
      dispatch(onGetPensionCalculationsAsync())
      return response
    } catch (err) {
      logger.error('Failed to delete pension calculation', err)
      return rejectWithValue({ message: err.message })
    }
  }
)

export const pensionSlice = createSlice({
  name: 'adminPensionCalculations',
  initialState,
  reducers: {
    goToEditPensionCalculation(state, action) {
      const { id } = action.payload
      state.projectToEdit = state.pensionCalculations.find(project => project.id === id)
    }
  },
  extraReducers: (builder) => {
    builder.addCase(onGetPensionCalculationsAsync.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(onGetPensionCalculationsAsync.fulfilled, (state, action) => {
      state.loading = false
      state.pensionCalculations = action.payload
      state.status = 'fulfilled'
    })
    builder.addCase(onGetPensionCalculationsAsync.rejected, (state, action) => {
      logger.debug('Fetch rejected:', action)
      state.loading = false
      state.error = action.payload?.message ?? action.error?.message ?? 'Unknown error'
    })
    builder.addCase(onEditPensionCalculationAsync.fulfilled, (state, action) => {
      state.pensionCalculations = action.payload
      state.status = 'fulfilled'
    })
    builder.addCase(onEditPensionCalculationAsync.rejected, (state, action) => {
      logger.debug('Edit rejected:', action)
      state.error = action.payload?.message ?? action.error?.message ?? 'Unknown error'
    })
    builder.addCase(onUpdatePensionCalculationStatusAsync.pending, (state, action) => {
      state.updatingId = action.meta.arg.id
      state.error = null
    })
    builder.addCase(onUpdatePensionCalculationStatusAsync.fulfilled, (state, action) => {
      state.updatingId = null
      const idx = state.pensionCalculations.findIndex(c => c.id === action.payload.id)
      if (idx !== -1) {
        state.pensionCalculations[idx].status = action.payload.status
        state.pensionCalculations[idx].paymentAt = formatFriendlyDate(action.payload.payment_at)
      }
    })
    builder.addCase(onUpdatePensionCalculationStatusAsync.rejected, (state, action) => {
      logger.debug('Update status rejected:', action)
      state.updatingId = null
      state.error = action.payload?.message ?? action.error?.message ?? 'Unknown error'
    })
    builder.addCase(onDeletePensionCalculationAsync.fulfilled, (state, action) => {
      state.pensionCalculations = state.pensionCalculations.filter(item => item.id !== action.payload.id)
      state.status = 'fulfilled'
    })
    builder.addCase(onDeletePensionCalculationAsync.rejected, (state, action) => {
      logger.debug('Delete rejected:', action)
      state.error = action.payload?.message ?? action.error?.message ?? 'Unknown error'
    })
  }
})

export const { goToEditPensionCalculation } = pensionSlice.actions

export default pensionSlice.reducer
